import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FC, useEffect, useMemo } from 'react';
import { Control, useFieldArray, UseFormClearErrors, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { DotsGridIcon, TrashBinIcon } from '../../../../../../assets/icons/index';
import InfoCircle from '../../../../../../assets/icons/info-circle';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import { SigningMode } from '../../../../templates/enums/template.const';
import {
  createQuickSignSignatorySlot,
  QUICK_SIGN_MAX_SIGNATORIES,
  QuickSignFormSchema,
} from '../../../validation/quick-sign.validation';
import { StakeholdersCombobox } from '../combobox/stakeholders-combobox';

type SignatorySlotCardProps = {
  companyId: string;
  control: Control<QuickSignFormSchema>;
  excludeStakeholderIds: string[];
  index: number;
  isOrderMode: boolean;
  onRemove: () => void;
  slotId: string;
};

const SignatorySlotCard: FC<SignatorySlotCardProps> = ({
  companyId,
  control,
  excludeStakeholderIds,
  index,
  isOrderMode,
  onRemove,
  slotId,
}) => {
  const comboboxName = `stepThree.signatorySlots.${index}.stakeholder` as const;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ disabled: !isOrderMode, id: slotId });

  return (
    <div
      className={twMerge(
        'flex w-full items-center rounded-lg bg-white px-3 py-3',
        isDragging && 'z-50 opacity-80 shadow-md',
        isOver && !isDragging && 'ring-2 ring-brand-500 ring-offset-1',
      )}
      ref={setNodeRef}
      style={{
        boxShadow:
          '0 -1px 3px 0 rgba(16, 24, 40, 0.04), 0 2px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)',
        ...(isOrderMode ? { transform: CSS.Transform.toString(transform), transition } : {}),
      }}
    >
      {isOrderMode && (
        <button
          className="mr-3 flex shrink-0 cursor-grab touch-none items-center text-gray-400 active:cursor-grabbing"
          type="button"
          {...listeners}
          {...attributes}
          aria-label="Drag to reorder"
        >
          <DotsGridIcon className="h-5 w-5" iconColor="#475467" />
        </button>
      )}

      <div className="flex shrink-0 items-center gap-2">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-25 text-xs font-normal text-brand-700">
          {index + 1}
        </span>

        <span className="shrink-0 text-sm font-medium text-gray-700">
          <AppFormattedMessage id="SIGNER_NUMBER" values={{ number: index + 1 }} />
        </span>
      </div>

      <StakeholdersCombobox
        className="ml-auto min-w-0 max-w-[195px] flex-1"
        companyId={companyId}
        control={control}
        excludeStakeholderIds={excludeStakeholderIds}
        name={comboboxName}
      />

      <button
        aria-label="Remove"
        className="ml-3 shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100"
        onClick={onRemove}
        type="button"
      >
        <TrashBinIcon className="h-4 w-4" iconColor="#A4A7AE" />
      </button>
    </div>
  );
};

export type QuickSignStepThreeProps = {
  clearErrors?: UseFormClearErrors<QuickSignFormSchema>;
  companyId: string;
  control: Control<QuickSignFormSchema>;
};

export const QuickSignStepThree: FC<QuickSignStepThreeProps> = ({
  clearErrors,
  companyId,
  control,
}) => {
  const signingMode = useWatch({ control, name: 'stepTwo.signingMode' });
  const watchedSignatorySlots = useWatch({ control, name: 'stepThree.signatorySlots' });
  const signatorySlots = useMemo(() => watchedSignatorySlots ?? [], [watchedSignatorySlots]);

  const { append, fields, move, remove } = useFieldArray({
    control,
    name: 'stepThree.signatorySlots',
  });

  const isOrderMode = signingMode === SigningMode.SET_ORDER;
  const isAtMax = fields.length >= QUICK_SIGN_MAX_SIGNATORIES;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const assignedStakeholderIds = useMemo(
    () =>
      signatorySlots
        .map((slot) => slot.stakeholder?.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    [signatorySlots],
  );

  useEffect(() => {
    clearErrors?.('stepThree.signatorySlots');
  }, [clearErrors]);

  const handleAddSlot = () => {
    if (isAtMax) return;
    append(createQuickSignSignatorySlot());
    queueMicrotask(() => {
      clearErrors?.('stepThree.signatorySlots');
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const fromIndex = fields.findIndex(({ slotId }) => slotId === active.id);
    const toIndex = fields.findIndex(({ slotId }) => slotId === over.id);
    if (fromIndex === -1 || toIndex === -1) return;

    move(fromIndex, toIndex);
  };

  return (
    <div className="flex h-full flex-col bg-gray-100">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-4 pt-6">
        <span className="text-xs text-gray-500">
          <AppFormattedMessage id={isOrderMode ? 'SIGNATORIES_ORDER' : 'SIGNATORIES'} />
        </span>

        {isOrderMode && (
          <p className="flex items-center rounded-md bg-sunbeam-50 px-3 py-2 text-xs text-gray-800">
            <AppFormattedMessage id="SIGNATORIES_ORDER_DRAG_HINT" />
          </p>
        )}

        {fields.length > 0 && (
          <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
            <SortableContext
              items={fields.map(({ slotId }) => slotId)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-3">
                {fields.map((field, index) => {
                  const excludeStakeholderIds = assignedStakeholderIds.filter(
                    (id) => id !== signatorySlots[index]?.stakeholder?.id,
                  );

                  return (
                    <SignatorySlotCard
                      companyId={companyId}
                      control={control}
                      excludeStakeholderIds={excludeStakeholderIds}
                      index={index}
                      isOrderMode={isOrderMode}
                      key={field.id}
                      onRemove={() => remove(index)}
                      slotId={field.slotId}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <button
          className="self-start pt-2 text-sm font-medium text-brand-700 underline disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isAtMax}
          onClick={handleAddSlot}
          type="button"
        >
          + <AppFormattedMessage id="ADD_SIGNER" />
        </button>

        {isAtMax && (
          <div className="flex items-center gap-1.5 text-xs text-fireside-600">
            <InfoCircle className="h-4 w-4 shrink-0" iconColor="#D92D20" />
            <AppFormattedMessage id="MAX_10_SIGNATORIES" />
          </div>
        )}
      </div>
    </div>
  );
};
